import { Suspense, useState } from 'react'

import { createFileRoute, redirect } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { isApiError } from '@/shared/api/utils/error'
import { Subtitle, Title } from '@/shared/auth/components/common/card'
import { AzureSignupForm } from '@/shared/auth/components/forms/signup-azure/form'
import { AzureSignupPanel } from '@/shared/auth/components/forms/signup-azure/panel'
import { authQueries } from '@/shared/auth/queries'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { Skeleton } from '@/components/ui/skeleton'

import type { ErrorComponentProps } from '@tanstack/react-router'

const searchSchema = z.object({
	token: z.string(),
})

export const Route = createFileRoute('/signup/azure/confirm')({
	validateSearch: zodValidator(searchSchema),

	loaderDeps: ({ search }) => {
		return {
			token: search.token,
		}
	},

	beforeLoad: async ({ context: { queryClient }, search }) => {
		const { token } = search
		const data = await queryClient.fetchQuery(
			authQueries.azureSignupDetails(token),
		)

		if (
			!!data.onboarding_state.tenant_id &&
			data.onboarding_state.user_linked
		) {
			throw redirect({
				to: '/home',
				replace: true,
			})
		}
	},

	loader: async ({ context: { queryClient }, deps }) => {
		const { token } = deps

		await queryClient.prefetchQuery(authQueries.azureSignupDetails(token))

		return {
			token,
		}
	},

	component: RouteComponent,
	pendingComponent: RoutePendingComponent,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const { token } = Route.useSearch()
	const [open, setOpen] = useState(false)

	return (
		<>
			<title>Confirm Subscription | BitLyft AIR®</title>

			<div className="relative flex w-full flex-row items-center justify-center gap-2">
				<Title>Confirm your subscription</Title>
				<ErrorBoundary>
					<Suspense>
						<AzureSignupPanel open={open} setOpen={setOpen} token={token} />
					</Suspense>
				</ErrorBoundary>
			</div>

			<Subtitle>
				Please confirm your subscription details to access BitLyft AIR®
			</Subtitle>

			<ErrorBoundary>
				<Suspense fallback={<Skeleton className="h-24 w-full" />}>
					<AzureSignupForm token={token} />
				</Suspense>
			</ErrorBoundary>
		</>
	)
}

function RoutePendingComponent() {
	return (
		<>
			<Title>Loading...</Title>
			<Skeleton className="mt-4 h-96 w-full" />
		</>
	)
}

function RouteErrorComponent({ error }: ErrorComponentProps) {
	const apiError = isApiError(error) ? error : undefined

	if (apiError?.status === 400) {
		return <div className="flex flex-col items-center">{apiError.message}</div>
	}

	return <div>Error</div>
}
